import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import DeviceIntegrationHub from "./DeviceIntegrationHub";
import AdminCorrectionFormContainer from "../components/AdminCorrectionFormContainer";

const {
  FiSettings,
  FiUsers,
  FiDatabase,
  FiShield,
  FiActivity,
  FiCpu,
  FiEdit2,
  FiTrash2,
  FiCreditCard,
  FiLock,
  FiSearch,
  FiRefreshCw,
  FiFilter,
  FiMapPin,
  FiShare2,
  FiCopy,
} = FiIcons;

const AdminDashboard = () => {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [auditLogs, setAuditLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState("all");
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [correctionTarget, setCorrectionTarget] = useState(null); // { patientId } | null

  const tabs = [
    { id: "overview", label: "Overview", icon: FiActivity },
    { id: "users", label: "User Management", icon: FiUsers },
    { id: "invitations", label: "Admin Invitations", icon: FiShare2 },
    { id: "security", label: "Security & Audit", icon: FiShield },
    { id: "devices", label: "Devices", icon: FiDatabase },
  ];

  useEffect(() => {
    if (activeTab === "users") fetchUsers();
    if (activeTab === "security") fetchAuditLogs();
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const res = await api.get("/users?role=patient");
      setUsers(Array.isArray(res.data) ? res.data : []);
      // console.log(res.data)
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setIsLoading(true);
      const res = await api.get("/admin/audit-logs");
      setAuditLogs(res.data);
    } catch (err) {
      console.error("Failed to fetch logs", err);
    } finally {
      setIsLoading(false);
    }
  };

  const generateAdminInvite = async () => {
    try {
      setIsLoading(true);
      const res = await api.post("/admin/invite", {});
      setInviteToken(res.data.inviteToken);
      setInviteUrl(res.data.inviteUrl);
    } catch (err) {
      console.error("Failed to generate admin invite", err);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const filteredLogs = auditLogs.filter((log) => {
    if (logFilter === "all") return true;
    if (logFilter === "mfa")
      return (
        log.action.toLowerCase().includes("mfa") ||
        log.resource.toLowerCase().includes("mfa")
      );
    if (logFilter === "geofence")
      return (
        log.action.toLowerCase().includes("geofence") ||
        log.resource.toLowerCase().includes("geofence")
      );
    return true;
  });

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-purple-100 text-purple-800";
      case "clinician":
        return "bg-blue-100 text-blue-800";
      case "patient":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiSettings} className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  iVivaCore Admin
                </h1>
                <p className="text-sm text-gray-600">
                  System administration and monitoring
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-full border border-green-200">
                <SafeIcon icon={FiLock} />
                <span>Secure Session</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.first_name} {user?.last_name}
                  </div>
                  <div className="text-xs text-gray-600 capitalize">
                    Administrator
                  </div>
                </div>
                <button
                  onClick={signOut}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
          <div className="min-w-max px-2 lg:px-6">
            <nav className="inline-flex items-center gap-2 whitespace-nowrap pb-2 sm:gap-4 lg:gap-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-shrink-0 items-center space-x-2 px-2 py-2 border-b-2 transition-colors sm:px-3 ${
                    activeTab === tab.id
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <SafeIcon icon={tab.icon} className="text-sm" />
                  <span className="font-medium text-xs">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* ── Devices tab ────────────────────────────────────────────────── */}
            {activeTab === "devices" ? (
              <DeviceIntegrationHub />
            ) : /* ── Admin Invitations ─────────────────────────────────────────── */
            activeTab === "invitations" ? (
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Generate Admin Invite
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    Create secure invitation tokens for new admin accounts. Each
                    token is unique, hashed, and expires after 7 days.
                  </p>
                  <button
                    onClick={generateAdminInvite}
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? "Generating..." : "Generate New Invite"}
                  </button>
                </div>

                {inviteToken && (
                  <div className="bg-white rounded-xl shadow-sm border border-green-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Invitation Created Successfully
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Invitation Token
                        </label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 p-3 bg-gray-100 rounded font-mono text-xs text-gray-900 break-all">
                            {inviteToken}
                          </code>
                          <button
                            onClick={() => copyToClipboard(inviteToken)}
                            className="p-2 hover:bg-gray-200 rounded transition-colors"
                            title="Copy token"
                          >
                            <SafeIcon icon={FiCopy} className="text-gray-600" />
                          </button>
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Invitation URL
                        </label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 p-3 bg-gray-100 rounded font-mono text-xs text-gray-900 break-all">
                            {inviteUrl}
                          </code>
                          <button
                            onClick={() => copyToClipboard(inviteUrl)}
                            className="p-2 hover:bg-gray-200 rounded transition-colors"
                            title="Copy URL"
                          >
                            <SafeIcon icon={FiCopy} className="text-gray-600" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 bg-yellow-50 p-3 rounded border border-yellow-200">
                        <p>
                          <strong>Note:</strong> Share the invitation URL or
                          token with the new admin candidate. The token will
                          expire in 7 days.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : /* ── Security & Audit ──────────────────────────────────────────── */
            activeTab === "security" ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-gray-500 text-sm font-medium">
                        Encryption Status
                      </h3>
                      <SafeIcon icon={FiLock} className="text-green-500" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      Active
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      AES-256 Data at Rest
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-gray-500 text-sm font-medium">
                        MFA Compliance
                      </h3>
                      <SafeIcon icon={FiShield} className="text-blue-500" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">High</div>
                    <div className="text-xs text-gray-500 mt-1">
                      TOTP Enforced for Admins
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-gray-500 text-sm font-medium">
                        Audit Pipeline
                      </h3>
                      <SafeIcon icon={FiActivity} className="text-purple-500" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {auditLogs.length}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Events indexed globally
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Security Logs
                    </h2>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center bg-gray-100 rounded-lg p-1">
                        {["all", "mfa", "geofence"].map((f) => (
                          <button
                            key={f}
                            onClick={() => setLogFilter(f)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-all ${
                              logFilter === f
                                ? "bg-white shadow-sm text-blue-600"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={fetchAuditLogs}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                      >
                        <SafeIcon icon={FiRefreshCw} />
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3">Timestamp</th>
                          <th className="px-6 py-3">Action</th>
                          <th className="px-6 py-3">Resource</th>
                          <th className="px-6 py-3">Status</th>
                          <th className="px-6 py-3">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 font-mono text-[11px] text-gray-500">
                              {new Date(log.timestamp).toLocaleString()}
                            </td>
                            <td className="px-6 py-3">
                              <div className="flex items-center gap-2">
                                {log.action.toLowerCase().includes("mfa") && (
                                  <SafeIcon
                                    icon={FiShield}
                                    className="text-blue-500"
                                  />
                                )}
                                {log.action
                                  .toLowerCase()
                                  .includes("geofence") && (
                                  <SafeIcon
                                    icon={FiMapPin}
                                    className="text-indigo-500"
                                  />
                                )}
                                <span className="font-medium text-gray-900">
                                  {log.action}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-3 text-gray-600">
                              {log.resource}
                            </td>
                            <td className="px-6 py-3">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  log.status === "success"
                                    ? "bg-green-100 text-green-800"
                                    : log.status === "denied" ||
                                        log.status === "failed"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {log.status}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-gray-500 truncate max-w-[300px]">
                              {log.details}
                            </td>
                          </tr>
                        ))}
                        {filteredLogs.length === 0 && (
                          <tr>
                            <td
                              colSpan="5"
                              className="px-6 py-8 text-center text-gray-500"
                            >
                              No logs found matching the current filter.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : /* ── User Management ───────────────────────────────────────────── */
            activeTab === "users" ? (
              correctionTarget ? (
                <AdminCorrectionFormContainer
                  patientId={correctionTarget.patientId}
                  onBack={() => setCorrectionTarget(null)}
                  onResolved={() => {
                    setCorrectionTarget(null);
                    fetchUsers();
                  }}
                />
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900">
                      User Management
                    </h2>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      RBAC Enforced
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-semibold">
                        <tr>
                          <th className="px-6 py-4">User</th>
                          <th className="px-6 py-4">Role</th>
                          <th className="px-6 py-4">Status</th>
                          <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {users.map((u) => (
                          <tr
                            key={u.id}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-600">
                                  {u.display_name?.[0]?.toUpperCase() ?? "?"}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {u.display_name || "—"}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {u.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`text-xs font-medium px-2 py-1 rounded-full ${getRoleColor(u.role)}`}
                              >
                                {u.role}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                              {u.patient_updated_at && (
                                <div
                                  className="text-[10px] text-gray-400 mt-1"
                                  title="Last update to this patient's demographic record (verification or admin correction) — not visible on platform_user itself"
                                >
                                  Updated{" "}
                                  {new Date(
                                    u.patient_updated_at,
                                  ).toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-right space-x-2">
                              {u.patient_id ? (
                                <button
                                  onClick={() =>
                                    setCorrectionTarget({
                                      patientId: u.patient_id,
                                    })
                                  }
                                  className="text-gray-400 hover:text-blue-600"
                                  title="Apply demographic correction"
                                >
                                  <SafeIcon icon={FiEdit2} />
                                </button>
                              ) : (
                                <span
                                  className="text-xs text-gray-400 italic"
                                  title="No canonical patient record yet — this user hasn't completed demographic verification"
                                >
                                  Not yet verified
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {users.length === 0 && !isLoading && (
                          <tr>
                            <td
                              colSpan="4"
                              className="px-6 py-8 text-center text-gray-500"
                            >
                              No users found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            ) : (
              /* ── Overview (and any future unbuilt tabs) ────────────────────── */
              <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
                <SafeIcon
                  icon={tabs.find((t) => t.id === activeTab)?.icon}
                  className="text-6xl text-gray-300 mx-auto mb-4"
                />
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  {tabs.find((t) => t.id === activeTab)?.label}
                </h3>
                <p className="text-gray-600">
                  Select another tab to view details.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;